
import React from 'react';

interface FeatureCardProps {
  icon: string;
  title: string;
  description: string;
  gradient: string;
}

const FeatureCard: React.FC<FeatureCardProps> = ({ icon, title, description, gradient }) => {
  return (
    <div className="bg-[#FAFBFC] rounded-3xl p-6 h-96 flex flex-col transition-transform duration-300 hover:transform hover:-translate-y-2">
      {/* Icon Area */}
      <div className={`${gradient} rounded-2xl h-48 flex items-center justify-center mb-6 relative overflow-hidden`}>
        <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent"></div>
        <div className="relative z-10 w-16 h-16 flex items-center justify-center">
          <i className={`${icon} text-4xl text-white`}></i>
        </div>
      </div>
      
      {/* Content */}
      <div className="flex-1 space-y-3">
        <h3 className="text-xl font-bold text-[#0A1628] leading-tight">
          {title}
        </h3>
        <p className="text-gray-600 leading-relaxed text-sm">
          {description}
        </p>
      </div>
    </div>
  );
};

export const FeaturesSection: React.FC = () => {
  const features = [
    {
      icon: 'ri-global-line',
      title: 'Global Server Network',
      description: 'Access our worldwide network of high-speed servers across 50+ countries for optimal performance and reliability.',
      gradient: 'bg-gradient-to-br from-[#1A4D7C] to-[#00D4FF]'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Zero Logs Policy',
      description: 'We never track, collect, or store your personal data. Your online activities remain completely private and anonymous.',
      gradient: 'bg-gradient-to-br from-[#0A1628] to-[#1E3A5F]'
    },
    {
      icon: 'ri-smartphone-line',
      title: 'Multi-Device Support',
      description: 'Protect all your devices with one account. Compatible with Windows, macOS, iOS, Android, and Linux systems.',
      gradient: 'bg-gradient-to-br from-[#2A5D8C] to-[#00D4FF]'
    },
    {
      icon: 'ri-time-line',
      title: '24/7 Protection',
      description: 'Round-the-clock security monitoring with automatic threat detection and real-time protection updates.',
      gradient: 'bg-gradient-to-br from-[#1E3A5F] to-[#1A4D7C]'
    }
  ];

  return (
    <section id="features" className="py-20 lg:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center mb-6">
            <div className="w-4 h-4 bg-[#1A4D7C] transform rotate-45 mr-3"></div>
            <span className="text-[#1A4D7C] font-medium text-sm uppercase tracking-wider">
              FEATURES
            </span>
          </div>
          
          <h2 className="text-4xl lg:text-6xl font-bold text-[#0A1628] mb-4">
            Why Choose BALESTRA VPN
          </h2>
          
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Comprehensive protection for all your devices
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {features.map((feature, index) => (
            <FeatureCard
              key={index}
              icon={feature.icon}
              title={feature.title}
              description={feature.description}
              gradient={feature.gradient}
            />
          ))}
        </div>

        {/* Additional Feature Highlight */}
        <div className="mt-20 bg-gradient-to-r from-[#F5F7FA] to-white rounded-3xl p-8 lg:p-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="text-3xl font-bold text-[#0A1628] mb-4">
                Enterprise-Grade Security
              </h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                BALESTRA VPN employs the same security protocols used by governments and 
                Fortune 500 companies. Our advanced encryption ensures that your sensitive 
                data remains protected against even the most sophisticated cyber threats.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-4 bg-white rounded-2xl shadow-sm">
                  <div className="text-2xl font-bold text-[#00D4FF] mb-1">256-bit</div>
                  <div className="text-xs text-gray-600 uppercase tracking-wider">Encryption</div>
                </div>
                <div className="text-center p-4 bg-white rounded-2xl shadow-sm">
                  <div className="text-2xl font-bold text-[#00D4FF] mb-1">50+</div>
                  <div className="text-xs text-gray-600 uppercase tracking-wider">Countries</div>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <img 
                src="https://public.readdy.ai/ai/img_res/036fc269eba744c11fda4706ca2fadad.jpg"
                alt="Security Dashboard Interface"
                className="w-full h-64 object-cover object-center rounded-2xl shadow-lg"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-[#1A4D7C]/20 to-transparent rounded-2xl"></div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
